<template>
  <BaseModule
    v-bind="$props"
    class="container simple-faq-module"
    :class="{ categories: showCategories }"
    data-module="simple-faq"
  >
    <div class="content">
      <header
        v-if="title || subtitle"
        class="content-title base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
      </header>

      <div class="content-questions">
        <div
          v-if="showCategories"
          class="content-icon-active-category"
          :style="{ top: arrowTopPosition }"
        >
          <i class="icon-arrow-left"></i>
        </div>
        <nav
          v-if="showCategories"
          class="menu-category"
        >
          <ul class="list-category">
            <li
              v-for="(category, index) in categories"
              :key="index"
              class="item-category-question"
              @click="setSelectedCategory(index)"
            >
              <div
                class="item-category"
                :style="{ backgroundColor: getCategoryBackgroundColor(index) }"
              >
                <div class="item-title-category base-content-fontsize">
                  <h4
                    class="color-hover-primary"
                    :style="{ color: getCategoryColor(index) }"
                  >
                    {{ category.category }}
                  </h4>
                </div>
              </div>
            </li>
          </ul>
        </nav>
        <div class="content-list">
          <div class="base-content-fontsize">
            <h4
              v-if="showCategories"
              class="list-questions-title"
            >
              {{ selectedCategoryLabel }}
            </h4>
          </div>
          <ul class="list-questions">
            <li
              v-for="(question, index) in filteredQuestions"
              :key="index"
              class="item-question"
              :class="{ active: checkIfDisplayAnswer(index) }"
            >
              <div
                class="item-title-question base-content-fontsize"
                @click="showAnswer(index)"
              >
                <h4>
                  {{ question.question }}
                </h4>
                <IconArrow
                  :direction="checkIfDisplayAnswer(index) ? 'down' : 'right'"
                />
              </div>

              <article
                v-show="checkIfDisplayAnswer(index)"
                class="content-answer"
              >
                <div class="answer base-content-fontsize">
                  <div
                    class="item-answer"
                    v-html="question.answer"
                  ></div>

                  <div
                    v-if="hasButton(question)"
                    class="item-link"
                  >
                    <EditorLink
                      v-if="showCTA(question.buttonText)"
                      :id="instanceId"
                      :model="question.linkUrl"
                      class="cta"
                    >
                      {{ question.buttonText }}
                    </EditorLink>
                  </div>
                </div>
              </article>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';

import base from '@/mixins/base';
import IconArrow from '@/components/icons/IconArrow.vue';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'SimpleFAQ',
  components: {
    BaseModule,
    EditorLink,
    IconArrow,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    categories: {
      type: Array,
      default() {
        return [];
      },
    },
    questions: {
      type: Array,
      default() {
        return [];
      },
    },
    showButton: {
      type: Boolean,
      default: false,
    },
    showCategories: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedQuestionIndex: '',
      selectedCategory: 0,
    };
  },
  computed: {
    filteredQuestions() {
      if (this.showCategories === true) {
        return this.questions.filter(
          (quest) => quest.questionCategory === this.selectedCategory,
        );
      }

      return this.questions;
    },
    arrowTopPosition() {
      const calcTop = 50 * this.selectedCategory + 16;
      return `${calcTop}px`;
    },
    selectedCategoryLabel() {
      let category = '';
      if (
        this.categories &&
        this.categories.length > 0 &&
        this.categories[this.selectedCategory].category
      ) {
        category = this.categories[this.selectedCategory].category;
      }
      return category;
    },
    ...mapGetters(['settings']),
  },
  methods: {
    showCTA(text) {
      return text !== '' && typeof text === 'string';
    },
    setSelectedCategory(categoryIndex) {
      this.selectedCategory = categoryIndex;
    },
    getCategoryBackgroundColor(categoryIndex) {
      if (this.selectedCategory === categoryIndex) {
        return this.settings.primaryColor;
      }

      return 'white';
    },
    getCategoryColor(categoryIndex) {
      if (this.selectedCategory === categoryIndex) {
        return 'white';
      }

      return '#888';
    },
    checkIfDisplayAnswer(id) {
      return id === this.selectedQuestionIndex;
    },
    showAnswer(id) {
      if (id === this.selectedQuestionIndex) {
        this.selectedQuestionIndex = '';
        return;
      }
      this.selectedQuestionIndex = id;
    },
    hasButton(question) {
      return question.showButton !== false;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'categories',
            label: 'Categorias',
            fields: [
              {
                name: 'showCategories',
                type: 'Checkbox',
                label: 'Incluir categorias',
              },
              {
                name: 'categories',
                type: 'List',
                minItems: 1,
                itemLabel({ category }) {
                  return category || 'Categoria {index}'; // any {index} will be replaced by item's index (1-based)
                },
                addItemLabel: 'Adicionar categoria',
                fields: [
                  {
                    name: 'category',
                    type: 'ShortText',
                    label: 'Categoria',
                  },
                ],
                isEnabled({ showCategories }) {
                  return showCategories;
                },
              },
            ],
          },
          {
            name: 'questions',
            label: 'Questões',
            fields: [
              {
                name: 'questions',
                type: 'List',
                minItems: 1,
                itemLabel({ question }) {
                  return question || 'Questão {index}'; // any {index} will be replaced by item's index (1-based)
                },
                addItemLabel: 'Adicionar questão',
                fields: [
                  {
                    name: 'questionCategory',
                    type: 'VueSelect',
                    label: 'Categoria',
                    default: '',
                    options: {
                      watch: true,
                      attribute: 'categories',
                      handler(categories) {
                        const categoriesOptions = categories.map(
                          (category, index) => ({
                            label: category.category,
                            value: index,
                          }),
                        );
                        return [
                          {
                            label: 'Selecione uma categoria',
                            value: '',
                          },
                          ...categoriesOptions,
                        ];
                      },
                    },
                    isEnabled({ showCategories }) {
                      return showCategories;
                    },
                  },
                  {
                    name: 'question',
                    type: 'LongText',
                    label: 'Pergunta',
                  },
                  {
                    name: 'answer',
                    type: 'Html',
                    label: 'Resposta',
                  },
                  {
                    name: 'showButton',
                    type: 'Checkbox',
                    label: 'Incluir botão na resposta',
                  },
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 35,
                    isEnabled() {
                      return this.showButton;
                    },
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                    isEnabled() {
                      return this.showButton;
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.simple-faq-module {
  padding: 0;
  padding-top: var(--padding-top-mobile);
  padding-bottom: var(--padding-bottom-mobile);

  .content-questions {
    width: 100%;
    padding: 20px;
    background: #f9f8f7;

    .content-list {
      padding: 0 15px;
    }
  }

  .list-questions {
    width: 100%;

    &.list-open {
      .item-question {
        &:not(.active) {
          opacity: 0.4;
          transition: 0.2 ease;
        }
      }
    }

    .item-question {
      display: flex;
      flex-wrap: wrap;
      border-radius: 3px;
      background: $lb-white;
      text-align: left;
      width: 100%;
      transition: 0.4s ease;

      &:not(:first-child) {
        margin: 2px 0 0 0;
      }

      &.active {
        .item-title-question {
          position: relative;

          h4 {
            color: var(--text-color, $lb-black);
            font-weight: $lb-bold;
            font-size: 1em;
          }
        }
      }

      .item-title-question {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 16px 15px;
        line-height: 20px;
        cursor: pointer;

        h4 {
          font-size: 1em;
          width: calc(100% - 30px);
          color: var(--text-color, $lb-black);
          font-weight: $lb-normal;
          margin: 0;
        }
      }
    }

    .content-answer {
      width: 100%;
      padding: 0 15px 1.5em 15px;
      font-size: 0.87em;
      line-height: 24px;

      .answer {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;

        .item-answer {
          display: flex;
          justify-content: flex-start;
          flex-wrap: wrap;
          width: 100%;
          text-align: left;
          color: var(--text-color);
        }

        .item-link {
          display: flex;
          justify-content: flex-start;
          flex-wrap: wrap;
          width: 100%;
          text-align: left;
          flex-wrap: nowrap;
          align-items: center;

          &:not(:first-child) {
            margin: 10px 0 0 0;
          }
        }
      }
    }
  }
}

@include media-breakpoint-up(lg) {
  .simple-faq-module {
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);

    .content-questions {
      padding: 32px 26px;

      .list-questions {
        .item-question {
          width: calc(100% - 10px);
          margin: 0 5px 0 5px;
          position: relative;
          z-index: 1;

          &.active {
            box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
          }

          &:not(.active) {
            .item-title-question {
              box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.05);
            }
          }

          &:not(:first-child) {
            margin: 2px 5px 0 5px;
          }

          &:last-child {
            margin: 2px 5px 5px 5px;
          }

          &:hover {
            box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
            z-index: 2;
          }

          .item-title-question {
            h4 {
              width: calc(100% - 30px);
            }
          }
        }

        &.list-open {
          .item-question {
            &:not(.active) {
              &:hover {
                opacity: 1;
                background: $lb-white;
                box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
              }
            }
          }
        }
      }
    }
  }
}

// Categories
.simple-faq-module.categories {
  .content-questions {
    background: $lb-white;
    display: flex;
    padding: 0;
    position: relative;

    .content-icon-active-category {
      display: flex;
      position: absolute;
      top: 0;
      left: calc(20% - 10px);
      z-index: 1;

      .icon-arrow-left {
        border-top: 10px solid transparent;
        border-right: 10px solid $lb-white;
        border-bottom: 10px solid transparent;
      }
    }

    .menu-category {
      position: relative;
      top: 0;
      left: 0;
      display: flex;
      width: 20%;
      max-height: 100%;
      min-height: 300px;
      background: $lb-white;

      .list-category {
        width: 100%;

        .item-category-question {
          .color-hover-primary {
            color: $lb-medium-gray;
            font-weight: $lb-normal;
            font-size: 1em;
            text-align: left;
            padding: 1em;
            margin: 0;
            cursor: pointer;
          }
        }
      }
    }
    .content-list {
      width: 80%;
      padding: 1em;
      box-shadow: -2px 4px 10px rgb(0 0 0 / 10%);

      .list-questions-title {
        font-size: 1.88em;
        font-weight: bold;
        text-align: left;
        padding: 16px;
        margin: 0;
      }

      .item-question {
        .item-title-question {
          box-shadow: none;
        }
      }
    }
  }
}

@include media-breakpoint-down(md) {
  .simple-faq-module.categories {
    .content-questions {
      .content-icon-active-category {
        display: flex;
        position: absolute;
        top: 0;
        left: calc(34% - 10px);
        z-index: 1;

        .icon-arrow-left {
          border-top: 10px solid transparent;
          border-right: 10px solid $lb-white;
          border-bottom: 10px solid transparent;
        }
      }

      .menu-category {
        width: 40%;

        .list-category {
          .item-category-question {
            .color-hover-primary {
              font-size: 0.87em;
            }
          }
        }
      }

      .content-list {
        padding: 1em 0;

        .list-questions-title {
          font-size: 1.13em;
        }
      }
    }
  }
}
</style>
